import { motion } from "framer-motion";
import { Timer, ShieldCheck, MapPin, Stethoscope, HeartHandshake } from "lucide-react";
import { Eyebrow, FadeUp, MaskLines } from "./Reveal";
import { COUNTIES } from "@/data/content";

const Tile = ({ className = "", children, testId, delay = 0 }) => (
  <FadeUp delay={delay} className={className}>
    <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 22 }} data-testid={testId}
      className="h-full rounded-3xl border border-white/10 bg-white/[0.04] p-7 md:p-9 flex flex-col justify-between gap-8 hover:border-sky-400/40 transition-colors">
      {children}
    </motion.div>
  </FadeUp>
);

export const WhyUs = () => (
  <section data-testid="why-us-section" className="relative bg-[#0A192F] text-white py-24 md:py-36 overflow-hidden grain">
    <div className="absolute inset-0 grid-lines" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <Eyebrow light>Why Trentvale</Eyebrow>
      <MaskLines className="mt-6 mb-14 md:mb-20 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.04em] leading-[0.95] max-w-3xl"
        lines={["The standards of a", <>national agency. The <span className="font-serif-i font-normal tracking-normal text-sky-300">warmth</span></>, "of a local one."]} />
      <div className="grid md:grid-cols-6 gap-4 md:gap-5">
        <Tile className="md:col-span-4" testId="why-us-bento-rapid">
          <Timer className="text-sky-300" size={28} />
          <div className="flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-12">
            <p className="text-7xl md:text-9xl font-extrabold tracking-[-0.06em] leading-none">[4]<span className="text-3xl md:text-5xl text-sky-300 tracking-tight">hrs</span></p>
            <p className="text-slate-300 max-w-xs leading-relaxed">Target time to place a briefed, compliant nurse on an urgent shift — day or night.</p>
          </div>
        </Tile>
        <Tile className="md:col-span-2" testId="why-us-bento-compliance" delay={0.08}>
          <ShieldCheck className="text-sky-300" size={28} />
          <div>
            <h3 className="text-2xl font-bold tracking-tight">Compliance first</h3>
            <p className="mt-3 text-slate-300 text-sm leading-relaxed">NMC PIN checks, enhanced DBS, mandatory training and references — verified before anyone is booked.</p>
          </div>
        </Tile>
        <Tile className="md:col-span-2" testId="why-us-bento-clinical" delay={0.04}>
          <Stethoscope className="text-sky-300" size={28} />
          <div>
            <h3 className="text-2xl font-bold tracking-tight">Clinical leads</h3>
            <p className="mt-3 text-slate-300 text-sm leading-relaxed">Every package and every client has a named nurse overseeing quality.</p>
          </div>
        </Tile>
        <Tile className="md:col-span-2" testId="why-us-bento-people" delay={0.08}>
          <HeartHandshake className="text-sky-300" size={28} />
          <div>
            <h3 className="text-2xl font-bold tracking-tight">Familiar faces</h3>
            <p className="mt-3 text-slate-300 text-sm leading-relaxed">We keep teams small and consistent, so people are cared for by people they know.</p>
          </div>
        </Tile>
        <Tile className="md:col-span-2" testId="why-us-bento-local" delay={0.12}>
          <MapPin className="text-sky-300" size={28} />
          <div>
            <h3 className="text-2xl font-bold tracking-tight">Truly local</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {COUNTIES.map((c) => <span key={c} className="text-xs rounded-full border border-white/15 px-3 py-1 text-slate-200">{c}</span>)}
            </div>
          </div>
        </Tile>
      </div>
    </div>
  </section>
);
